import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CellContext } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { BillingAttempt, BILLING_ATTEMPT_STATUS } from 'src/features/billingAttempts/utils/types';
import { useRetryBillingAttempt } from 'src/features/billingAttempts/api/useRetryBillingAttempt';
import { LIST_BILLING_ATTEMPTS_RESOURCE } from 'src/features/billingAttempts/api/useListBillingAttempts';
import { billingAttemptsMessages } from 'src/features/billingAttempts/utils/messages';

export default function CellBillingAttemptActions({ row }: CellContext<BillingAttempt, unknown>) {
    const { formatMessage } = useIntl();
    const permissionManageCharges = useHasPermissions([PERMISSION.MANAGE_CHARGES]);
    const onError = useOnError();

    const { mutate: retry, isPending } = useRetryBillingAttempt({
        onSuccess: (attempt) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_BILLING_ATTEMPTS_RESOURCE] });

            /*
             * An unchanged id means the item was no longer due and the original attempt came back
             * untouched. Reporting that as a success would show a green "retried!" for work that
             * never happened - exactly what an ops screen exists to prevent.
             */
            const nothingToDo = attempt.id === row.original.id;
            toast({
                title: formatMessage(
                    nothingToDo ? billingAttemptsMessages.retryNothingToDo : billingAttemptsMessages.retrySuccess,
                ),
                description: formatMessage(
                    nothingToDo
                        ? billingAttemptsMessages.retryNothingToDoDescription
                        : billingAttemptsMessages.retrySuccessDescription,
                ),
            });
        },
        // Without this a failed retry was silent: the button re-enabled and nothing said why. A 409
        // means the subscription or customer is being billed right now and the retry can be repeated
        // once that finishes.
        onError: (error) => onError(error),
    });

    const isRetryable =
        row.original.status === BILLING_ATTEMPT_STATUS.FAILED || row.original.status === BILLING_ATTEMPT_STATUS.SKIPPED;

    if (!permissionManageCharges || !isRetryable) return null;

    return (
        <div className="flex w-full justify-end gap-1">
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            size="icon"
                            variant="link"
                            disabled={isPending}
                            className="cursor-pointer hover:bg-gray-100"
                            onClick={() => retry({ attemptId: row.original.id })}
                        >
                            <FontAwesomeIcon icon={ICON_MAP.ROTATE} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <FormattedMessage {...billingAttemptsMessages.retry} />
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}
